import { CardHeader as ContentCardHeader } from '@hue/design-system';
import { UserRound as CardIconUserRound, Building2 as CardIconBuilding2 } from 'lucide-react';
import { Button, Modal, useSnackbar } from '@hue/design-system';
import { Building2, Clock3 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { getApiError, preferencesApi, type SessionUser } from '../lib/api.js';
import { resolveTimeZone } from '../lib/time-zone.js';
import { TimeZoneSelect } from './time-zone-select.js';

export function TimeZoneSettings({
  open,
  onOpenChange,
  user,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: SessionUser;
  onSaved: () => Promise<void>;
}) {
  const [userTimeZone, setUserTimeZone] = useState(() =>
    resolveTimeZone(user.timeZone, user.currentTenant?.timeZone),
  );
  const companyDefault =
    user.currentTenant?.defaultTimeZone ?? user.currentTenant?.timeZone ?? 'UTC';
  const [tenantTimeZone, setTenantTimeZone] = useState(companyDefault);
  const [saving, setSaving] = useState<'USER' | 'TENANT'>();
  const snackbar = useSnackbar();
  const wasOpen = useRef(false);
  const isRoot = user.platformRole === 'ROOT';

  useEffect(() => {
    if (open && !wasOpen.current) {
      setUserTimeZone(resolveTimeZone(user.timeZone, user.currentTenant?.timeZone));
      setTenantTimeZone(companyDefault);
    }
    wasOpen.current = open;
  }, [open, companyDefault, user.currentTenant?.timeZone, user.timeZone]);

  async function saveUserTimeZone() {
    setSaving('USER');
    try {
      await preferencesApi.updateUserTimeZone(userTimeZone);
      await onSaved();
      snackbar.success('Seu fuso horário foi atualizado.');
    } catch (requestError) {
      snackbar.failure(getApiError(requestError));
    } finally {
      setSaving(undefined);
    }
  }

  async function saveTenantTimeZone() {
    setSaving('TENANT');
    try {
      await preferencesApi.updateTenantTimeZone(tenantTimeZone);
      await onSaved();
      snackbar.success('O fuso padrão de novas unidades foi atualizado.');
    } catch (requestError) {
      snackbar.failure(getApiError(requestError));
    } finally {
      setSaving(undefined);
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Fuso horário"
      description="Escolha como datas e horários devem ser interpretados e exibidos."
    >
      <div className="space-y-5">
        <section className="rounded-2xl border border-surface-border bg-surface-elevated/45 p-4 sm:p-5">
          <div className="mb-4">
            <ContentCardHeader
              title="Sua hora local"
              level={3}
              icon={<CardIconUserRound className="size-4" />}
            />
            <p className="mt-1 text-xs leading-5 text-content-muted">
              Define como datas e horários aparecem para você em todas as empresas que acessa.
            </p>
          </div>
          <TimeZoneSelect
            label="Meu fuso horário"
            value={userTimeZone}
            onValueChange={setUserTimeZone}
            disabled={Boolean(saving)}
            showTechnicalDetails={isRoot}
          />
          <div className="mt-4 flex justify-end">
            <Button
              className="w-full sm:w-auto"
              type="button"
              variant="outline"
              loading={saving === 'USER'}
              disabled={Boolean(saving)}
              onClick={() => void saveUserTimeZone()}
            >
              <Clock3 className="size-4" />
              Salvar meu fuso
            </Button>
          </div>
        </section>

        {user.currentTenant?.canManageSettings ? (
          <section className="rounded-2xl border border-brand-500/20 bg-brand-500/5 p-4 sm:p-5">
            <div className="mb-4">
              <ContentCardHeader
                title="Padrão de novas unidades"
                level={3}
                icon={<CardIconBuilding2 className="size-4" />}
              />
              <p className="mt-1 text-xs leading-5 text-content-muted">
                O fuso com que cada unidade nova de {user.currentTenant.displayName} começa. Agenda,
                vencimentos e fechamentos seguem o fuso de cada unidade, que se ajusta em
                Configurações, Unidades.
              </p>
            </div>
            <TimeZoneSelect
              label="Fuso padrão de novas unidades"
              value={tenantTimeZone}
              onValueChange={setTenantTimeZone}
              disabled={Boolean(saving)}
              showTechnicalDetails={isRoot}
            />
            <div className="mt-4 flex justify-end">
              <Button
                className="w-full sm:w-auto"
                type="button"
                loading={saving === 'TENANT'}
                disabled={Boolean(saving)}
                onClick={() => void saveTenantTimeZone()}
              >
                <Building2 className="size-4" />
                Salvar fuso padrão
              </Button>
            </div>
          </section>
        ) : null}
      </div>
    </Modal>
  );
}
